import {
  CollectionReference,
  DocumentData,
  DocumentReference,
  Firestore,
  OrderByDirection,
  Query,
  WhereFilterOp,
  collection,
  doc,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  InsuranceCaseData,
  InsuranceClaimData,
  ClioSyncAttemptBatchData,
  ClioSyncAttemptData,
  CommunicationEventData,
  EngagementLetterData,
  EventData,
  ExpenseEntryData,
  FinancialInstitutionData,
  FirmData,
  HumanData,
  InvoiceAllocationCoordinatorData,
  InvoiceData,
  InvoiceTransactionAllocationData,
  InvoiceNumberCounterData,
  InvoiceEmailData,
  InvoicePresentationSnapshotData,
  InvoiceTimelineEventData,
  KnowledgeBasePageData,
  MatterDraftInvoiceState,
  MatterData,
  MatterInvoiceBalanceEventData,
  MatterDocumentData,
  NoteData,
  OrganizationData,
  PaySheetData,
  TaskData,
  TemplateData,
  TimeEntryData,
  TransactionData,
  UserData,
} from './baseTypes';
import {
  insuranceCaseConverter,
  insuranceClaimConverter,
  clioSyncAttemptBatchConverter,
  clioSyncAttemptConverter,
  communicationEventConverter,
  engagementLetterConverter,
  eventConverter,
  expenseEntryConverter,
  financialInstitutionConverter,
  firmConverter,
  generalConverter,
  humanConverter,
  invoiceAllocationCoordinatorConverter,
  invoiceConverter,
  invoiceNumberCounterConverter,
  invoicePresentationSnapshotConverter,
  invoiceTimelineEventConverter,
  invoiceTransactionAllocationConverter,
  knowledgeBasePageConverter,
  matterConverter,
  matterInvoiceBalanceEventConverter,
  matterDraftInvoiceStateConverter,
  matterDocumentConverter,
  noteConverter,
  organizationConverter,
  paySheetConverter,
  taskConverter,
  templateConverter,
  timeEntryConverter,
  transactionConverter,
  userConverter,
} from './converters';

export class QueryBuilder {
  firmId: string;
  firestore: Firestore;

  constructor(firmId: string, firestore: Firestore) {
    this.firmId = firmId;
    this.firestore = firestore;
  }

  /**
   * `firms/{firmId}`
   */
  firm(): DocumentReference<FirmData> {
    return doc(
      collection(this.firestore, 'firms').withConverter(firmConverter),
      this.firmId,
    );
  }

  /**
   * `firms/{firmId}/clioSyncAttempts`
   */
  clioSyncAttempts(): CollectionReference<ClioSyncAttemptData> {
    return collection(this.firm(), 'clioSyncAttempts').withConverter(
      clioSyncAttemptConverter,
    );
  }

  /**
   * `firms/{firmId}/clioSyncAttempts/{attemptId}`
   */
  clioSyncAttempt(attemptId: string): DocumentReference<ClioSyncAttemptData> {
    this.assertExists(attemptId, 'attemptId is required');
    return doc(this.clioSyncAttempts(), attemptId);
  }

  /**
   * `firms/{firmId}/clioSyncAttempts/{attemptId}/batches`
   */
  clioSyncAttemptBatches(
    attemptId: string,
  ): CollectionReference<ClioSyncAttemptBatchData> {
    this.assertExists(attemptId, 'attemptId is required');
    return collection(this.clioSyncAttempt(attemptId), 'batches').withConverter(
      clioSyncAttemptBatchConverter,
    );
  }

  /**
   * `firms/{firmId}/phoneCalls`
   */
  phoneCalls(
    where: WhereClause<CommunicationEventData>[] = [],
  ): CollectionReference<CommunicationEventData> {
    return this.applyWhereClauses(
      collection(this.firm(), 'phoneCalls').withConverter(
        communicationEventConverter,
      ),
      where,
    );
  }

  /**
   * `firms/{firmId}/phoneCalls/{callId}`
   */
  phoneCall(callId: string): DocumentReference<CommunicationEventData> {
    this.assertExists(callId, 'callId is required');
    return doc(this.phoneCalls(), callId);
  }

  /**
   * `firms/{firmId}/textMessages`
   */
  textMessages(
    where: WhereClause<CommunicationEventData>[] = [],
  ): CollectionReference<CommunicationEventData> {
    return this.applyWhereClauses(
      collection(this.firm(), 'textMessages').withConverter(
        communicationEventConverter,
      ),
      where,
    );
  }

  /**
   * `firms/{firmId}/textMessages/{messageId}`
   */
  textMessage(messageId: string): DocumentReference<CommunicationEventData> {
    this.assertExists(messageId, 'messageId is required');
    return doc(this.textMessages(), messageId);
  }

  /**
   * `firms/{firmId}/engagementLetters`
   */
  engagementLetters(
    where: WhereClause<EngagementLetterData>[] = [],
    orderBy: OrderByClause<EngagementLetterData>[] = [],
  ): CollectionReference<EngagementLetterData> {
    const collectionQuery = collection(
      this.firm(),
      'engagementLetters',
    ).withConverter(engagementLetterConverter);
    return this.applyWhereAndOrderByClauses(collectionQuery, where, orderBy);
  }

  /**
   * `firms/{firmId}/engagementLetters/{letterId}`
   */
  engagementLetter(letterId: string): DocumentReference<EngagementLetterData> {
    this.assertExists(letterId, 'letterId is required');
    return doc(this.engagementLetters(), letterId);
  }

  /**
   * `firms/{firmId}/events`
   *
   * Matter-related events written by cloud functions in
   * `cloudFunctions/src/functions/onMatterCreate.ts` and
   * `onMatterStatusUpdate.ts`. Each event has a `matterId` field, so callers
   * filter by `[['matterId', '==', matterId]]` to get a single matter's events.
   */
  events(
    where: WhereClause<EventData>[] = [],
    orderBy: OrderByClause<EventData>[] = [],
  ): CollectionReference<EventData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'events').withConverter(eventConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/expenseEntries`
   */
  expenseEntries(
    where: WhereClause<ExpenseEntryData>[] = [],
    orderBy: OrderByClause<ExpenseEntryData>[] = [],
  ): CollectionReference<ExpenseEntryData> {
    const collectionQuery = collection(
      this.firm(),
      'expenseEntries',
    ).withConverter(expenseEntryConverter);
    return this.applyWhereAndOrderByClauses(collectionQuery, where, orderBy);
  }

  /**
   * `firms/{firmId}/expenseEntries/{entryId}`
   */
  expenseEntry(entryId: string): DocumentReference<ExpenseEntryData> {
    this.assertExists(entryId, 'entryId is required');
    return doc(this.expenseEntries(), entryId);
  }

  /**
   * `firms/{firmId}/financialInstitutions`
   */
  financialInstitutions(
    where: WhereClause<FinancialInstitutionData>[] = [],
    orderBy: OrderByClause<FinancialInstitutionData>[] = [],
  ): CollectionReference<FinancialInstitutionData> {
    const collectionQuery = collection(
      this.firm(),
      'financialInstitutions',
    ).withConverter(financialInstitutionConverter);
    return this.applyWhereAndOrderByClauses(collectionQuery, where, orderBy);
  }

  /**
   * `firms/{firmId}/financialInstitutions/{institutionId}`
   */
  financialInstitution(
    institutionId: string,
  ): DocumentReference<FinancialInstitutionData> {
    this.assertExists(institutionId, 'institutionId is required');
    return doc(this.financialInstitutions(), institutionId);
  }

  /**
   * `firms/{firmId}/humans`
   */
  humans(
    where: WhereClause<HumanData>[] = [],
    orderBy: OrderByClause<HumanData>[] = [],
  ): CollectionReference<HumanData> {
    const collectionQuery = collection(this.firm(), 'humans').withConverter(
      humanConverter,
    );
    return this.applyWhereAndOrderByClauses(collectionQuery, where, orderBy);
  }

  /**
   * `firms/{firmId}/humans/{humanId}`
   */
  human(humanId: string): DocumentReference<HumanData> {
    this.assertExists(humanId, 'humanId is required');
    return doc(this.humans(), humanId);
  }

  /**
   * `firms/{firmId}/invoices`
   */
  invoices(
    where: WhereClause<InvoiceData>[] = [],
    orderBy: OrderByClause<InvoiceData>[] = [],
  ): CollectionReference<InvoiceData> {
    const collectionQuery = collection(this.firm(), 'invoices').withConverter(
      invoiceConverter,
    );
    return this.applyWhereAndOrderByClauses(collectionQuery, where, orderBy);
  }

  /**
   * `firms/{firmId}/invoices/{invoiceId}`
   */
  invoice(invoiceId: string): DocumentReference<InvoiceData> {
    this.assertExists(invoiceId, 'invoiceId is required');
    return doc(this.invoices(), invoiceId);
  }

  /**
   * `firms/{firmId}/invoiceTransactionAllocations`
   *
   * Server-owned transaction/invoice edges. This read path does not establish
   * that ordinary allocation behavior is enabled; writers must still validate
   * the firm's cutover state and the reviewed allocation rules.
   */
  invoiceTransactionAllocations(
    where: WhereClause<InvoiceTransactionAllocationData>[] = [],
    orderBy: OrderByClause<InvoiceTransactionAllocationData>[] = [],
  ): CollectionReference<InvoiceTransactionAllocationData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'invoiceTransactionAllocations').withConverter(
        invoiceTransactionAllocationConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/invoiceTransactionAllocations/{allocationId}`
   */
  invoiceTransactionAllocation(
    allocationId: string,
  ): DocumentReference<InvoiceTransactionAllocationData> {
    this.assertExists(allocationId, 'allocationId is required');
    return doc(this.invoiceTransactionAllocations(), allocationId);
  }

  /**
   * `firms/{firmId}/system/invoiceAllocationCoordinator`
   *
   * One firm-wide revision counter used to detect allocation-affecting races.
   * It is not a client-acquired lock and must never be client-written.
   */
  invoiceAllocationCoordinator(): DocumentReference<InvoiceAllocationCoordinatorData> {
    return doc(
      collection(this.firm(), 'system').withConverter(
        invoiceAllocationCoordinatorConverter,
      ),
      'invoiceAllocationCoordinator',
    );
  }

  /**
   * `firms/{firmId}/config/invoiceNumberCounter`
   */
  invoiceNumberCounter(): DocumentReference<InvoiceNumberCounterData> {
    return doc(
      collection(this.firm(), 'config').withConverter(
        invoiceNumberCounterConverter,
      ),
      'invoiceNumberCounter',
    );
  }

  /**
   * `firms/{firmId}/invoices/{invoiceId}/presentationSnapshots`
   */
  invoicePresentationSnapshots(
    invoiceId: string,
    orderBy: OrderByClause<InvoicePresentationSnapshotData>[] = [],
  ): CollectionReference<InvoicePresentationSnapshotData> {
    this.assertExists(invoiceId, 'invoiceId is required');
    const collectionQuery = collection(
      this.invoice(invoiceId),
      'presentationSnapshots',
    ).withConverter<InvoicePresentationSnapshotData>(
      invoicePresentationSnapshotConverter,
    );
    return this.applyWhereAndOrderByClauses(collectionQuery, [], orderBy);
  }

  /**
   * `firms/{firmId}/invoices/{invoiceId}/presentationSnapshots/{snapshotId}`
   */
  invoicePresentationSnapshot(
    invoiceId: string,
    snapshotId: string,
  ): DocumentReference<InvoicePresentationSnapshotData> {
    this.assertExists(snapshotId, 'presentationSnapshotId is required');
    return doc(this.invoicePresentationSnapshots(invoiceId), snapshotId);
  }

  /**
   * `firms/{firmId}/views/invoicesList`
   */
  invoicesListView(): DocumentReference<DocumentData> {
    return doc(this.firm(), 'views', 'invoicesList').withConverter(
      generalConverter<DocumentData>(),
    );
  }

  /**
   * `firms/{firmId}/views/invoicesList/rows`
   */
  invoicesListRows(): CollectionReference<DocumentData> {
    return collection(this.invoicesListView(), 'rows').withConverter(
      generalConverter<DocumentData>(),
    );
  }

  invoicesListMatterUpdates(): CollectionReference<DocumentData> {
    return collection(this.invoicesListView(), 'matterUpdates').withConverter(
      generalConverter<DocumentData>(),
    );
  }

  /**
   * `firms/{firmId}/knowledgeBasePages`
   */
  knowledgeBasePages(
    where: WhereClause<KnowledgeBasePageData>[] = [],
    orderBy: OrderByClause<KnowledgeBasePageData>[] = [],
  ): CollectionReference<KnowledgeBasePageData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'knowledgeBasePages').withConverter(
        knowledgeBasePageConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/knowledgeBasePages/{pageId}`
   */
  knowledgeBasePage(pageId: string): DocumentReference<KnowledgeBasePageData> {
    this.assertExists(pageId, 'pageId is required');
    return doc(this.knowledgeBasePages(), pageId);
  }

  /**
   * `firms/{firmId}/matters`
   */
  matters(
    where: WhereClause<MatterData>[] = [],
    orderBy: OrderByClause<MatterData>[] = [],
  ): CollectionReference<MatterData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'matters').withConverter(matterConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/matters/{matterId}`
   */
  matter(matterId: string): DocumentReference<MatterData> {
    this.assertExists(matterId, 'matterId is required');
    return doc(this.matters(), matterId);
  }

  /**
   * `firms/{firmId}/matterInvoiceBalanceEvents`
   *
   * Append-only audit records for carried-forward anchors. These are separate
   * from the general `events` collection and are server-owned.
   */
  matterInvoiceBalanceEvents(
    where: WhereClause<MatterInvoiceBalanceEventData>[] = [],
    orderBy: OrderByClause<MatterInvoiceBalanceEventData>[] = [],
  ): CollectionReference<MatterInvoiceBalanceEventData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'matterInvoiceBalanceEvents').withConverter(
        matterInvoiceBalanceEventConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/matterInvoiceBalanceEvents/{eventId}`
   */
  matterInvoiceBalanceEvent(
    eventId: string,
  ): DocumentReference<MatterInvoiceBalanceEventData> {
    this.assertExists(eventId, 'matterInvoiceBalanceEventId is required');
    return doc(this.matterInvoiceBalanceEvents(), eventId);
  }

  /**
   * `firms/{firmId}/invoiceDraftStates`
   */
  matterDraftInvoiceStates(): CollectionReference<MatterDraftInvoiceState> {
    return collection(this.firm(), 'invoiceDraftStates').withConverter(
      matterDraftInvoiceStateConverter,
    );
  }

  /**
   * `firms/{firmId}/invoiceDraftStates/{matterId}`
   */
  matterDraftInvoiceState(
    matterId: string,
  ): DocumentReference<MatterDraftInvoiceState> {
    this.assertExists(matterId, 'matterId is required');
    return doc(this.matterDraftInvoiceStates(), matterId);
  }

  /**
   * `firms/{firmId}/matters/{matterId}/documents`
   */
  matterDocuments(
    matterId: string,
    where: WhereClause<MatterDocumentData>[] = [],
    orderBy: OrderByClause<MatterDocumentData>[] = [],
  ): CollectionReference<MatterDocumentData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.matter(matterId), 'documents').withConverter(
        matterDocumentConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/matters/{matterId}/notes`
   */
  matterNotes(
    matterId: string,
    where: WhereClause<NoteData>[] = [],
    orderBy: OrderByClause<NoteData>[] = [],
  ): CollectionReference<NoteData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.matter(matterId), 'notes').withConverter(noteConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/insuranceCases`
   */
  insuranceCases(
    where: WhereClause<InsuranceCaseData>[] = [],
    orderBy: OrderByClause<InsuranceCaseData>[] = [],
  ): CollectionReference<InsuranceCaseData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'insuranceCases').withConverter(
        insuranceCaseConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/insuranceCases/{caseId}`
   */
  insuranceCase(caseId: string): DocumentReference<InsuranceCaseData> {
    this.assertExists(caseId, 'caseId is required');
    return doc(this.insuranceCases(), caseId);
  }

  /**
   * `firms/{firmId}/insuranceClaims`
   */
  insuranceClaims(
    where: WhereClause<InsuranceClaimData>[] = [],
    orderBy: OrderByClause<InsuranceClaimData>[] = [],
  ): CollectionReference<InsuranceClaimData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'insuranceClaims').withConverter(
        insuranceClaimConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/insuranceClaims/{claimId}`
   */
  insuranceClaim(claimId: string): DocumentReference<InsuranceClaimData> {
    this.assertExists(claimId, 'claimId is required');
    return doc(this.insuranceClaims(), claimId);
  }

  /**
   * `firms/{firmId}/organizations`
   */
  organizations(
    where: WhereClause<OrganizationData>[] = [],
    orderBy: OrderByClause<OrganizationData>[] = [],
  ): CollectionReference<OrganizationData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'organizations').withConverter(
        organizationConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/organizations/{organizationId}`
   */
  organization(organizationId: string): DocumentReference<OrganizationData> {
    this.assertExists(organizationId, 'organizationId is required');
    return doc(this.organizations(), organizationId);
  }

  /**
   * `firms/{firmId}/tasks`
   */
  tasks(
    where: WhereClause<TaskData>[] = [],
    orderBy: OrderByClause<TaskData>[] = [],
  ): CollectionReference<TaskData> {
    const collectionQuery = collection(this.firm(), 'tasks').withConverter(
      taskConverter,
    );
    return this.applyWhereAndOrderByClauses(collectionQuery, where, orderBy);
  }

  /**
   * `firms/{firmId}/tasks/{taskId}`
   */
  task(taskId: string): DocumentReference<TaskData> {
    this.assertExists(taskId, 'taskId is required');
    return doc(this.tasks(), taskId);
  }

  /**
   * `firms/{firmId}/templates`
   */
  templates(
    where: WhereClause<TemplateData>[] = [],
    orderBy: OrderByClause<TemplateData>[] = [],
  ): CollectionReference<TemplateData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'templates').withConverter(templateConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/templates/{templateId}`
   */
  template(templateId: string): DocumentReference<TemplateData> {
    this.assertExists(templateId, 'templateId is required');
    return doc(this.templates(), templateId);
  }

  /**
   * `firms/{firmId}/timeEntries`
   */
  timeEntries(
    where: WhereClause<TimeEntryData>[] = [],
    orderBy: OrderByClause<TimeEntryData>[] = [],
  ): CollectionReference<TimeEntryData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'timeEntries').withConverter(timeEntryConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/timeEntries/{entryId}`
   */
  timeEntry(entryId: string): DocumentReference<TimeEntryData> {
    this.assertExists(entryId, 'entryId is required');
    return doc(this.timeEntries(), entryId);
  }

  /**
   * `firms/{firmId}/transactions`
   */
  transactions(
    where: WhereClause<TransactionData>[] = [],
    orderBy: OrderByClause<TransactionData>[] = [],
  ): CollectionReference<TransactionData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'transactions').withConverter(
        transactionConverter,
      ),
      where,
      orderBy,
    );
  }

  transaction(id: string): DocumentReference<TransactionData> {
    this.assertExists(id, 'Transaction ID is required');
    return doc(this.transactions(), id);
  }

  /**
   * `firms/{firmId}/users`
   */
  users(
    where: WhereClause<UserData>[] = [],
    orderBy: OrderByClause<UserData>[] = [],
  ): CollectionReference<UserData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'users').withConverter(userConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/users/{userId}`
   */
  user(userId: string): DocumentReference<UserData> {
    this.assertExists(userId, 'userId is required');
    return doc(this.users(), userId);
  }

  /**
   * `firms/{firmId}/paySheets`
   */
  paySheets(
    where: WhereClause<PaySheetData>[] = [],
    orderBy: OrderByClause<PaySheetData>[] = [],
  ): CollectionReference<PaySheetData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'paySheets').withConverter(paySheetConverter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/paySheets/{paySheetId}`
   */
  paySheet(paySheetId: string): DocumentReference<PaySheetData> {
    this.assertExists(paySheetId, 'paySheetId is required');
    return doc(this.paySheets(), paySheetId);
  }

  /**
   * `firms/{firmId}/invoiceEmails`
   */
  invoiceEmails(
    where: WhereClause<InvoiceEmailData>[] = [],
    orderBy: OrderByClause<InvoiceEmailData>[] = [],
  ): CollectionReference<InvoiceEmailData> {
    const converter = generalConverter<InvoiceEmailData>();
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'invoiceEmails').withConverter(converter),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/invoiceEmails/{emailId}`
   */
  invoiceEmail(emailId: string): DocumentReference<InvoiceEmailData> {
    this.assertExists(emailId, 'emailId is required');
    return doc(this.invoiceEmails(), emailId);
  }

  /**
   * `firms/{firmId}/invoiceTimelineEvents`
   */
  invoiceTimelineEvents(
    where: WhereClause<InvoiceTimelineEventData>[] = [],
    orderBy: OrderByClause<InvoiceTimelineEventData>[] = [],
  ): CollectionReference<InvoiceTimelineEventData> {
    return this.applyWhereAndOrderByClauses(
      collection(this.firm(), 'invoiceTimelineEvents').withConverter(
        invoiceTimelineEventConverter,
      ),
      where,
      orderBy,
    );
  }

  /**
   * `firms/{firmId}/invoiceTimelineEvents/{eventId}`
   */
  invoiceTimelineEvent(
    eventId: string,
  ): DocumentReference<InvoiceTimelineEventData> {
    this.assertExists(eventId, 'eventId is required');
    return doc(this.invoiceTimelineEvents(), eventId);
  }

  // Utilities

  applyWhereAndOrderByClauses<
    DataType extends DocumentData,
    QueryType extends Query<DataType>,
  >(
    query: QueryType,
    where: WhereClause<DataType>[],
    orderBy: OrderByClause<DataType>[],
  ): QueryType {
    return this.applyOrderByClauses(
      this.applyWhereClauses(query, where),
      orderBy,
    );
  }

  applyWhereClauses<
    DataType extends DocumentData,
    QueryType extends Query<DataType>,
  >(origQuery: QueryType, whereClauses?: WhereClause<DataType>[]): QueryType {
    return (whereClauses ?? []).reduce((aggregateQuery, clause) => {
      const [fieldPath, opStr, value] = clause;
      return query(aggregateQuery, where(fieldPath as string, opStr, value));
    }, origQuery as Query) as QueryType;
  }

  applyOrderByClauses<
    DataType extends DocumentData,
    QueryType extends Query<DataType>,
  >(
    origQuery: QueryType,
    orderByClauses?: OrderByClause<DataType>[],
  ): QueryType {
    return (orderByClauses ?? []).reduce((aggregateQuery, stmt) => {
      const [fieldPath, directionStr] = stmt;
      return query(aggregateQuery, orderBy(fieldPath as string, directionStr));
    }, origQuery as Query) as QueryType;
  }

  assertExists(value: any, message: string) {
    if (!value) {
      throw new Error(message);
    }
  }
}

/**
 * Every legal Firestore field-path string for a document type `T`: every
 * top-level key, plus dot-notation paths into nested object fields
 * (e.g. `_autoGeneratedFields.clientContactIds`). Optional fields are
 * unwrapped via `NonNullable`, and `Date` / array fields are leaves
 * (Firestore doesn't address inside them via dot-notation).
 *
 * `Depth` is a recursion fuse — TypeScript bails out of unbounded
 * recursion. 5 is plenty for every document shape in this codebase.
 *
 * The point of this type (rather than a looser `keyof T | string`) is
 * that typos and field renames break compile across every WhereClause /
 * OrderByClause caller.
 */
type DocumentFieldPath<T, Depth extends number = 5> = [Depth] extends [0]
  ? never
  : T extends Date | Array<unknown>
    ? never
    : T extends object
      ? {
          [K in Extract<keyof T, string>]:
            | K
            | (NonNullable<T[K]> extends Date | Array<unknown>
                ? never
                : NonNullable<T[K]> extends object
                  ? `${K}.${DocumentFieldPath<NonNullable<T[K]>, Decrement<Depth>>}`
                  : never);
        }[Extract<keyof T, string>]
      : never;

type Decrement<N extends number> = [never, 0, 1, 2, 3, 4][N];

export type WhereClause<T> = [
  fieldPath: DocumentFieldPath<T>,
  opStr: WhereFilterOp,
  value: any,
];

export type OrderByClause<T> = [
  fieldPath: DocumentFieldPath<T>,
  directionStr?: OrderByDirection,
];
